import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    FormHelperText,
    Heading,
    HStack,
    Image,
    Input,
    SimpleGrid,
    Spinner,
    Text,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';

import { agencyService, AgencyProfile, AgencyBrandInput } from '../../../services/agencyService';

type Field = keyof AgencyBrandInput;

// brandLogoUrl gets its own control (file upload); the rest are plain inputs.
const FIELDS: { key: Field; label: string; placeholder: string; help: string }[] = [
    { key: 'brandName', label: 'Brand name', placeholder: 'FitLab', help: 'Replaces "TrackHQ" across the client app, emails and notifications.' },
    { key: 'brandPrimaryColor', label: 'Primary colour', placeholder: '#A15FDC', help: 'Hex, e.g. #A15FDC.' },
    { key: 'brandSupportEmail', label: 'Support email', placeholder: 'help@fitlab.com', help: 'Shown on the help screen; client email replies go here.' },
];

const EMPTY: Record<Field, string> = {
    brandName: '', brandPrimaryColor: '', brandLogoUrl: '', brandSupportEmail: '',
};

const MAX_LOGO_BYTES = 256 * 1024;

/// Editor for the "your own branded app" tier. `context="trainer"` makes it
/// read-only for a gym's staff (only the gym account edits its brand); a solo
/// trainer's workspace is independent, so they still edit.
export default function BrandingSettings({ context = 'agency' }: { context?: 'agency' | 'trainer' }) {
    const cardBg = useColorModeValue('white', 'navy.700');
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();

    const [profile, setProfile] = useState<AgencyProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState<Record<Field, string>>(EMPTY);
    const fileRef = useRef<HTMLInputElement>(null);

    const pickLogo = (file?: File) => {
        if (!file) return;
        if (file.size > MAX_LOGO_BYTES) {
            toast({ title: `Logo is ${Math.round(file.size / 1024)} KB; keep it under 256 KB`, status: 'error', duration: 4000, isClosable: true });
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setForm((f) => ({ ...f, brandLogoUrl: String(reader.result) }));
        reader.readAsDataURL(file);
    };

    useEffect(() => {
        agencyService
            .getAgencyProfile()
            .then((p) => {
                setProfile(p);
                setForm({
                    brandName: p.brandName,
                    brandPrimaryColor: p.brandPrimaryColor,
                    brandLogoUrl: p.brandLogoUrl,
                    brandSupportEmail: p.brandSupportEmail,
                });
            })
            .catch(() => setProfile(null))
            .then(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            setProfile(await agencyService.updateBranding(form));
            toast({ title: 'Branding saved', status: 'success', duration: 3000, isClosable: true });
        } catch (e: any) {
            toast({
                title: 'Error',
                description: e.message || 'Failed to save branding',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box p="20px" bg={cardBg} borderRadius="20px">
                <Spinner size="sm" />
            </Box>
        );
    }

    // A gym's staff can see the brand but only the gym account edits it.
    const readOnly = context === 'trainer' && !!profile && !profile.isIndependent;

    return (
        <Box p="20px" bg={cardBg} borderRadius="20px">
            <Heading size="md" color={textColor} mb={profile?.brandingEnabled ? 4 : 2}>
                Your own branded app
            </Heading>
            {!profile?.brandingEnabled ? (
                <Text color={textColor}>
                    Put your name, logo and colours in the app your clients already use. Not on your
                    plan yet — contact support to turn it on.
                </Text>
            ) : readOnly ? (
                <Text color={textColor}>
                    Your gym sets the branding for everyone. Brand: <b>{profile.brandName || '—'}</b>,
                    colour <b>{profile.brandPrimaryColor || '—'}</b>.
                </Text>
            ) : (
                <>
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
                        {FIELDS.map((f) => (
                            <FormControl key={f.key}>
                                <FormLabel>{f.label}</FormLabel>
                                <Input
                                    value={form[f.key]}
                                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                    placeholder={f.placeholder}
                                />
                                <FormHelperText>{f.help}</FormHelperText>
                            </FormControl>
                        ))}
                        <FormControl>
                            <FormLabel>Logo</FormLabel>
                            <HStack spacing={3} align="center">
                                {form.brandLogoUrl && (
                                    <Image src={form.brandLogoUrl} alt="logo" maxH="40px" maxW="120px" fallback={<Box />} />
                                )}
                                <Button size="sm" onClick={() => fileRef.current?.click()}>
                                    {form.brandLogoUrl ? 'Replace' : 'Upload'}
                                </Button>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                                    hidden
                                    onChange={(e) => pickLogo(e.target.files?.[0])}
                                />
                            </HStack>
                            <FormHelperText>
                                PNG, JPEG, SVG or WebP, under 256 KB. Shown on the loading, header and help screens.
                            </FormHelperText>
                        </FormControl>
                    </SimpleGrid>
                    <Button mt={4} colorScheme="brand" onClick={save} isLoading={saving}>
                        Save branding
                    </Button>
                </>
            )}
        </Box>
    );
}
